import { faRobot } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  CheckboxGroupComponent, type CheckboxGroupItem,
} from '~/_shared/baseComponents/checkbox/checkboxGroup.component';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { UnderlinedLinkComponent } from '~/_shared/baseComponents/links';
import {
  ExportDataModalComponent,
  type ExportDataModalResults,
} from '~/_shared/components/exportDataModal/exportDataModal.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { prepareAsyncCopyToClipboard } from '~/_shared/utils/clipboard/clipboard.helpers';
import { downloadFile } from '~/_shared/utils/document/document.helpers';
import { getExportUrl } from '~/_shared/utils/export/export.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { useTimeout } from '~/_shared/utils/hooks/useTimeout';
import { useGetBoundaryNameOverrides } from '~/boundary/useGetBoundaryNameOverrides';
import { ExportDataFileType } from '~/data/exportDataModal/exportDataFileType.enum';
import { ExportDataMethod } from '~/data/exportDataModal/exportDataMethod.enum';
import { type ModalProps } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { trialMessageStyle } from '~/sidebar/sidebarApps/mapTools/boundary/export/exportBoundaryTerritoryGroup.styles';
import { type SpreadsheetDataBoundariesRequest } from '~/spreadsheet/spreadsheet.repository';
import { isSuccessBulkSpreadsheet } from '~/spreadsheet/typeguards';
import { useCheckBoundaryIsCustom } from '~/store/boundaryGroups/boundaryGroups.selectors';
import {
  createRequestFromBoundaryTerritoryGroupSettings, getBoundaryTerritoryGroupMatchupRequest,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.factory';
import { ensureBeValidBoundaryTerritoryGroupId } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.helpers';
import {
  boundaryIsOptimizedTerritoriesTrialSelector, useActiveBoundaryTerritoryGroupsSelector,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { closeAllModals } from '~/store/modal/modal.actionCreators';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import {
  s, Trans,
} from '~/translations/Trans';
import { useBoundaryTerritoryGroupDisplayName } from '../hooks/useBoundaryTerritoryGroupDisplayName';
import { useExportBoundaryTerritoryGroupData } from '../hooks/useExportBoundaryTerritoryGroupData';

export type ExportBoundaryTerritoryGroupContainerProps = ModalProps<{
  selectedBoundaryTerritoryGroupIds: ReadonlyArray<number>;
  showSelectBoundaryTerritoryGroupsSection: boolean;
}>;

export const ExportBoundaryTerritoryGroupContainer: FC<ExportBoundaryTerritoryGroupContainerProps> = (props) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedGroupsIds, setSelectedGroupsIds] = useState(props.selectedBoundaryTerritoryGroupIds);
  const [isCopiedToClipboard, showCopiedToClipboardMessage] = useTimeout(3000);
  const { openModal: openDownloadResultsModal } = useModal('DownloadResults');
  const { isLoading, isError, getExportedBoundaryTerritoryGroupData } = useExportBoundaryTerritoryGroupData();
  const activeBoundaryTerritoryGroups = useActiveBoundaryTerritoryGroupsSelector();
  const { getBoundaryTerritoryGroupName } = useBoundaryTerritoryGroupDisplayName();
  const virtualSpreadsheetId = usePrimarySpreadsheetId();
  const { getBoundaryNameOverrides } = useGetBoundaryNameOverrides();
  const [t] = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { openModal: openUpgradeSubscriptionModal } = useModal('WMSUpgradeSubscription');
  const [isSelectionSectionExpanded, setIsSelectionSectionExpanded] = useState(true);
  const { checkBoundaryIsCustom } = useCheckBoundaryIsCustom();
  const checkIfBoundaryIsOptimizedTerritoriesTrial = useSelector(boundaryIsOptimizedTerritoriesTrialSelector);

  const onSave = async (arg: ExportDataModalResults) => {
    if (!virtualSpreadsheetId || selectedGroupsIds.length === 0) {
      return;
    }
    const asyncCopyToClipboard = prepareAsyncCopyToClipboard();
    setErrorMessage(null);

    const selectedBoundaryTerritoryGroups = activeBoundaryTerritoryGroups
      .filter(group => selectedGroupsIds.includes(group.boundaryTerritoryGroupId));

    const nameOverridesList = await Promise.all(selectedBoundaryTerritoryGroups.map(
      btg => checkBoundaryIsCustom(btg.boundaryGroupId)
        ? getBoundaryNameOverrides(btg.boundaryGroupId)
        : Promise.resolve({})
    ));
    const boundariesRequest = nameOverridesList.reduce((acc: SpreadsheetDataBoundariesRequest, overrides, index) => {
      const boundaryTerritoryGroup = selectedBoundaryTerritoryGroups[index];
      if (!boundaryTerritoryGroup) {
        return acc;
      }

      const matchings = getBoundaryTerritoryGroupMatchupRequest(boundaryTerritoryGroup.matchings);

      const includeBTColumnDefinition = boundaryTerritoryGroup.settings.boundaryTerritories.length ? {
        boundary_territory_group_id: ensureBeValidBoundaryTerritoryGroupId(boundaryTerritoryGroup),
        boundary_group_id: boundaryTerritoryGroup.boundaryGroupId,
        matchings,
        settings: createRequestFromBoundaryTerritoryGroupSettings(boundaryTerritoryGroup.settings),
      } : undefined;

      return {
        ...acc,
        [boundaryTerritoryGroup.boundaryGroupId]: {
          name_overrides: overrides,
          matchings,
          definition: includeBTColumnDefinition,
        },
      };
    }, {});

    const response = await getExportedBoundaryTerritoryGroupData({
      spreadsheetId: virtualSpreadsheetId,
      boundariesRequest,
      extension: arg.method === ExportDataMethod.Clipboard ? ExportDataFileType.Csv : arg.fileType,
      exportAsText: arg.method === ExportDataMethod.Clipboard,
      useMainFilters: true,
    });

    const spreadsheet = response.data[0];

    if (arg.method === ExportDataMethod.Clipboard) {
      const fileContent = isSuccessBulkSpreadsheet(spreadsheet) ? spreadsheet.result?.content : undefined;

      if (!fileContent) {
        setErrorMessage(t('No locations to export'));
        return;
      }

      asyncCopyToClipboard(fileContent);
      showCopiedToClipboardMessage();
    }
    else {
      const fileName = isSuccessBulkSpreadsheet(spreadsheet) ? spreadsheet.result?.file_name : undefined;

      if (!fileName) {
        setErrorMessage(t('No locations to export'));
        return;
      }

      const exportUrl = getExportUrl(fileName);
      downloadFile(exportUrl);

      props.onClose();
      openDownloadResultsModal({
        downloadUrl: exportUrl,
      });
    }
  };

  const boundaryTerritoryGroupOptions = useMemo<CheckboxGroupItem<number>[]>(() => {
    return activeBoundaryTerritoryGroups.map(group => {
      const isTemporaryTerritory = checkIfBoundaryIsOptimizedTerritoriesTrial(group.boundaryGroupId);

      return ({
        value: group.boundaryTerritoryGroupId,
        text: getBoundaryTerritoryGroupName(group),
        isDisabled: isTemporaryTerritory,
        extraContent: isTemporaryTerritory && (
          <div css={trialMessageStyle({ theme })}>
            <FontAwesomeIcon
              icon={faRobot}
            />
            <Trans i18nKey="<0>Upgrade here</0> to enable export">
              <UnderlinedLinkComponent
                onClick={() => {
                  dispatch(closeAllModals());
                  openUpgradeSubscriptionModal();
                }}
              >
                Upgrade here
              </UnderlinedLinkComponent>
              {s(' to enable export')}
            </Trans>
          </div>
        ),
      });
    });
  }, [activeBoundaryTerritoryGroups, checkIfBoundaryIsOptimizedTerritoriesTrial, getBoundaryTerritoryGroupName, theme, dispatch, openUpgradeSubscriptionModal]);

  useEffect(() => {
    if (isError) {
      setErrorMessage(t('Error exporting data'));
    }
  }, [isError, t]);

  useEffect(() => {
    setErrorMessage(null);
  }, [selectedGroupsIds]);

  const sections = props.showSelectBoundaryTerritoryGroupsSection ? [{
    header: t('Choose the boundary / territory groups you want to export'),
    isExpanded: isSelectionSectionExpanded,
    onHeadingClick: () => setIsSelectionSectionExpanded(!isSelectionSectionExpanded),
    child: (
      <CheckboxGroupComponent
        onSelectionChanged={setSelectedGroupsIds}
        selectedValues={selectedGroupsIds}
        items={boundaryTerritoryGroupOptions}
      />),
  }] : undefined;

  return (
    <ExportDataModalComponent
      isLoading={isLoading}
      sectionsBeforeExportMethod={sections}
      onSubmit={onSave}
      onClose={props.onClose}
      isOpen={props.isOpen}
      error={errorMessage}
      isSubmitDisabled={selectedGroupsIds.length === 0}
      showCopiedToClipboardTooltip={isCopiedToClipboard}
      noticeTrialUsers
    />
  );
};
